import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/Input';
import Button from '../components/Button';
import { organizerBusinessDetailsService } from "../services/organizerBusinessDetailsService";

const InfoIcon = ({ text }: { text: string }) => (
    <div className="st-tooltip-wrapper ml-2 cursor-help relative top-1">
        <svg className="w-4 h-4 text-[var(--st-text-muted)] hover:text-[var(--st-accent-primary)] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="st-tooltip w-64 whitespace-normal text-center bg-[#0f172a] text-white p-2 rounded text-xs z-50">
            {text}
        </div>
    </div>
);

const OrganizerBusinessDetails = () => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        id: '',
        email: '',
        organizationName: '',
        contactNumber: '',
        firstName: '',
        lastName: '',
        organizationDescription: '',
        organizationLogo: ''
    });

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isFetching, setIsFetching] = useState(true);

    // Redirección de seguridad
    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            if (user?.role !== 'user-organizer') {
                navigate('/');
            }
        }
    }, [user, isAuthenticated, isLoading, navigate]);

    const fetchProfileData = useCallback(async () => {
        if (user?.email) {
            try {
                const backendData = await organizerBusinessDetailsService.getUserByEmail(user.email);
                setFormData({
                    id: backendData.id || user.userId || '',
                    email: backendData.email || user.email,
                    organizationName: backendData.organizationName || '',
                    contactNumber: backendData.contactNumber || '',
                    firstName: backendData.firstName || '',
                    lastName: backendData.lastName || '',
                    organizationDescription: backendData.organizationDescription || '',
                    organizationLogo: backendData.organizationLogo || ''
                });

                if (backendData.organizationLogo) {
                    setLogoPreview(backendData.organizationLogo);
                }
            } catch (error) {
                console.error("No se pudieron cargar los datos del organizador", error);
            }
        }
    }, [user]);

    // Carga inicial
    useEffect(() => {
        if (isAuthenticated && !isLoading) {
            setIsFetching(true);
            fetchProfileData().finally(() => setIsFetching(false));
        }
    }, [isAuthenticated, isLoading, fetchProfileData]);

    // Limpieza de memoria
    useEffect(() => {
        return () => {
            if (logoPreview && logoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(logoPreview);
            }
        };
    }, [logoPreview]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!['image/jpeg', 'image/png'].includes(file.type)) {
                alert('Solo se permiten archivos .jpg o .png');
                return;
            }
            setLogoFile(file);
            const objectUrl = URL.createObjectURL(file);
            setLogoPreview(objectUrl);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await organizerBusinessDetailsService.updateUser(formData, logoFile);
            await fetchProfileData();
            setLogoFile(null);
            alert("Información de perfil actualizada correctamente");
        } catch (error) {
            console.error("Error en submit:", error);
            alert("Error al guardar los cambios.");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading || isFetching) {
        return <div className="flex justify-center py-20"><div className="st-spinner st-spinner-lg"></div></div>;
    }

    return (
        <div className="st-page py-12">
            <div className="st-container w-full max-w-5xl mx-auto">

                <div className="flex items-center justify-between mb-8 gap-6">

                    <div className="flex-1">
                        <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)]">Perfil del Organizador</h1>
                        <p className="text-[var(--st-text-secondary)]">Genera confianza completando la información de tu organización, subiendo tu logotipo y definiendo tus datos de contacto.</p>
                    </div>

                    <div className="hidden sm:flex items-center justify-center w-24 h-24 rounded-full border-4 border-[var(--st-bg-card)] shadow-lg bg-[var(--st-bg-tertiary)] overflow-hidden relative flex-shrink-0">
                        {logoPreview ? (
                            <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-cover" />
                        ) : (
                            <div className="flex flex-col items-center text-[var(--st-text-muted)]">
                                <span className="text-2xl">📷</span>
                                <span className="text-[10px]">Sin Logo</span>
                            </div>
                        )}
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8 animate-fade-in w-full">

                    <div className="st-card p-6 md:p-8 w-full">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-[var(--st-accent-primary-light)] text-[var(--st-accent-primary)] flex items-center justify-center font-bold">
                                🏢
                            </div>
                            <h2 className="font-display text-xl font-bold">Información General</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Organization Name */}
                            <div>
                                <label className="st-label st-label-required flex items-center">
                                    Nombre de la Organización
                                    <InfoIcon text="Es el nombre público que verán los compradores en tus eventos." />
                                </label>
                                <Input
                                    name="organizationName"
                                    value={formData.organizationName}
                                    onChange={handleChange}
                                    placeholder="Ej: Eventos Bogotá S.A.S"
                                    required
                                />
                            </div>

                            {/* Contact Number */}
                            <div>
                                <label className="st-label st-label-required flex items-center">
                                    WhatsApp de Contacto
                                    <InfoIcon text="Número para que tus clientes te contacten." />
                                </label>
                                <Input
                                    name="contactNumber"
                                    value={formData.contactNumber}
                                    onChange={handleChange}
                                    placeholder="Ej: 573001234567"
                                    type="tel"
                                    required
                                    icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label className="st-label st-label-required flex items-center">
                                    E-mail
                                    <InfoIcon text="Usuario de acceso (No editable)." />
                                </label>
                                <Input
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={true}
                                    className="opacity-75 cursor-not-allowed"
                                />
                            </div>

                            <div className="hidden md:block"></div>

                            {/* First Name */}
                            <div>
                                <label className="st-label st-label-required flex items-center">
                                    Nombre de contacto
                                    <InfoIcon text="Nombre de contacto en la organización" />
                                </label>
                                <Input
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    placeholder="Ej: Daniel Felipe"
                                    required
                                    icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                                />
                            </div>

                            {/* Last Name */}
                            <div>
                                <label className="st-label st-label-required flex items-center">
                                    Apellidos de contacto
                                    <InfoIcon text="Apellido de contacto en la organización" />
                                </label>
                                <Input
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    placeholder="Ej: Gonzalez Mojica"
                                    required
                                    icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                                />
                            </div>

                            {/* LOGO UPLOAD */}
                            <div className="md:col-span-2">
                                <label className="st-label flex items-center st-label-required">
                                    Logotipo de la Organización
                                </label>
                                <div className="st-input p-1 flex items-center justify-between gap-3 group hover:border-[var(--st-accent-primary)] transition-colors">
                                    <div className="flex items-center gap-3 px-2 overflow-hidden">
                                        <div className="w-8 h-8 rounded bg-[var(--st-bg-tertiary)] flex items-center justify-center flex-shrink-0 text-[var(--st-accent-primary)]">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                        </div>
                                        <span className={`text-sm truncate ${logoFile ? 'text-[var(--st-text-primary)] font-medium' : 'text-[var(--st-text-muted)]'}`}>
                                            {logoFile ? logoFile.name : "Selecciona una imagen (JPG, PNG)..."}
                                        </span>
                                    </div>
                                    <label className="st-btn st-btn-secondary st-btn-sm cursor-pointer whitespace-nowrap">
                                        Examinar
                                        <input
                                            type="file"
                                            accept=".jpg, .jpeg, .png"
                                            className="hidden"
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="md:col-span-2">
                                <label className="st-label st-label-required flex items-center">
                                    Descripción
                                    <InfoIcon text="Cuenta un poco sobre quiénes son y qué tipo de eventos realizan." />
                                </label>
                                <textarea
                                    name="organizationDescription"
                                    value={formData.organizationDescription}
                                    onChange={handleChange}
                                    rows={4}
                                    className="st-input st-textarea w-full"
                                    placeholder="Somos una empresa dedicada a..."
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-4">
                        <Button type="button" variant="ghost" onClick={() => navigate('/organizer/dashboard')}>
                            Cancelar
                        </Button>
                        <Button type="submit" className="st-btn st-btn-primary st-btn-md" variant="primary" disabled={isSaving}>
                            {isSaving ? 'Guardando...' : 'Guardar Perfil'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OrganizerBusinessDetails;